/**
 * IndividualCoursePage — public sign-up page for the ARP Individual (Pleasure
 * or Problem?) five-day email course.  Marketing lead magnet, no auth required.
 *
 * Route: /pleasure-or-problem
 *
 * Identical structure to FamilyCoursePage: captures UTM params from the query
 * string and posts them to /api/individual-course/signup which tags the
 * subscriber with `course_stream="individual-arp"`.
 */
import React, { useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Mail, Loader2, ShieldCheck } from 'lucide-react';

export default function IndividualCoursePage() {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const utm = useMemo(() => ({
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
  }), [params]);

  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [sensitiveConsent, setSensitiveConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState('idle'); // idle | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !sensitiveConsent) return;
    setSubmitting(true);
    setErrorMsg('');
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/individual-course/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          first_name: firstName.trim() || null,
          secondary_marketing_consent: marketing,
          sensitive_info_consent: sensitiveConsent,
          consent_version: 'v1-2026-02',
          ...utm,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.ok) {
        throw new Error(data.detail || 'Sign-up failed. Please try again.');
      }
      setState('success');
    } catch (err) {
      setErrorMsg(err.message || 'Sign-up failed. Please try again.');
      setState('error');
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'success') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4 py-12">
        <div className="max-w-lg w-full text-center" data-testid="individual-course-confirmation">
          <img src="/logo512.png" alt="Axiom" className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-4 leading-tight">
            You're in: check your inbox
          </h1>
          <p className="text-zinc-600 text-base leading-relaxed mb-3">
            The confirmation email is on its way (usually within a few minutes).
            You'll receive one short email a day for the next five days.
          </p>
          <p className="text-zinc-600 text-sm leading-relaxed mb-8">
            We read every reply: usually within one business day. Reply to any email in the series if there's
            anything you want to say. Or write to us directly at{' '}
            <a href="mailto:recovery@tudor8.com" className="text-zinc-900 underline underline-offset-2">
              recovery@tudor8.com
            </a>.
          </p>
          <p className="text-xs text-zinc-500">
            Didn't get anything after 15 minutes? Check your spam folder, then let us know at
            {' '}<a href="mailto:hello@tudor8.com" className="underline">hello@tudor8.com</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <img src="/logo512.png" alt="Axiom" className="w-14 h-14 mb-8" />

        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-3">
          Free five-day email course
        </p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 leading-[1.1] mb-5">
          Pleasure or Problem?
        </h1>
        <p className="text-base sm:text-lg text-zinc-700 leading-relaxed mb-10">
          A free five-day email course for anyone who has started to wonder whether their own drinking or
          drug use has crossed a line. Straight answers, no lecture, no scare tactics.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="individual-course-signup-form">
          <div>
            <label htmlFor="first-name" className="sr-only">First name (optional)</label>
            <input
              id="first-name"
              type="text"
              placeholder="First name (optional)"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={80}
              autoComplete="given-name"
              className="w-full px-4 py-3 border border-zinc-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
              data-testid="individual-course-first-name"
            />
          </div>
          <div>
            <label htmlFor="email" className="sr-only">Email address</label>
            <input
              id="email"
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full px-4 py-3 border border-zinc-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
              data-testid="individual-course-email"
            />
          </div>

          <label className="flex items-start gap-3 py-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sensitiveConsent}
              onChange={(e) => setSensitiveConsent(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
              data-testid="individual-course-sensitive-consent"
              required
            />
            <span className="text-sm text-zinc-700 leading-relaxed">
              <strong>Consent required (Privacy Act 1988, APP 3.3).</strong> I understand this course
              addresses alcoholism / addiction and that by signing up I am giving Tudor8 my express
              consent to hold my email address in a private database used only to deliver this course
              and to reply if I write in. It will not be shared or sold.
            </span>
          </label>

          <label className="flex items-start gap-3 py-2 cursor-pointer">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
              data-testid="individual-course-marketing-checkbox"
            />
            <span className="text-sm text-zinc-700 leading-relaxed">
              Also keep me updated about the <strong>Axiom Recovery Programme</strong> and other free resources.
            </span>
          </label>

          {state === 'error' && (
            <div
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
              data-testid="individual-course-error"
            >
              {errorMsg}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !email.trim() || !sensitiveConsent}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-zinc-900 text-white font-bold rounded-lg text-base hover:bg-zinc-800 active:scale-[0.99] transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="individual-course-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                Start the Free Course
              </>
            )}
          </button>
        </form>

        <p className="text-xs text-zinc-500 leading-relaxed mt-6">
          You'll get one short email a day for five days. Unsubscribe any time.
        </p>

        <div className="mt-8 p-4 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-600 leading-relaxed space-y-2">
          <p className="font-bold text-zinc-800 text-[11px] uppercase tracking-wider">Collection notice (Privacy Act 1988, APP 5)</p>
          <p><strong>Who is collecting your information:</strong> Tudor8 (Australia).</p>
          <p><strong>Why:</strong> to deliver this free five-day email course and, if you tick the box above, occasional updates about the Axiom Recovery Programme.</p>
          <p><strong>Who else it is disclosed to:</strong> only our transactional email provider (Resend) so the emails actually arrive. It is not shared, sold or used for advertising.</p>
          <p><strong>Sensitive information:</strong> because this course addresses alcoholism / addiction, your sign-up may reveal health information. That's why we ask for express consent above.</p>
          <p><strong>Your rights:</strong> you can access, correct or delete the data we hold about you at any time by emailing <a href="mailto:recovery@tudor8.com" className="underline">recovery@tudor8.com</a>. Retention: while you are subscribed, plus 24 months after unsubscribe for legal-defence purposes, then hard-deleted.</p>
          <p>Full <Link to="/privacy-policy" className="underline hover:text-zinc-800">privacy policy</Link>.</p>
        </div>

        <div className="mt-6 pt-6 border-t border-zinc-100 flex items-start gap-3 text-xs text-zinc-500 leading-relaxed">
          <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p>
            Every email includes a one-click unsubscribe. This form is served over HTTPS and the underlying
            database is private to Tudor8.
          </p>
        </div>
      </div>
    </div>
  );
}
